import { RootState } from '@/store/store'
import { createSelector } from '@reduxjs/toolkit'
import { ViewerOpenContextKind, type ViewerOpenContext } from './schemas'

export const selectActiveContentViewerSession = (state: RootState) => state.contentViewerState.session

export const selectIsContentViewerOpen = createSelector(selectActiveContentViewerSession, (session) => Boolean(session))

type ActiveContentViewerContextMatch =
  | { kind: ViewerOpenContextKind.CHAT_ARTIFACT; artifactVersionId: string }
  | { kind: ViewerOpenContextKind.AUTO_OPEN; artifactVersionId: string }
  | { kind: ViewerOpenContextKind.CHAT_CITATION; citationId: string; highlightIndex?: number }
  | { kind: ViewerOpenContextKind.MED_CHRON_REFERENCE; referenceId: string }
  | { kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE; referenceId: string }
  | { kind: ViewerOpenContextKind.TIMELINE_REFERENCE; referenceId: string }
  | { kind: ViewerOpenContextKind.MED_CHRON_SOURCE; sourceId: string }
  | { kind: ViewerOpenContextKind.MED_SUMMARY_SOURCE; sourceId: string }
  | { kind: ViewerOpenContextKind.TIMELINE_SOURCE; sourceId: string }

function activeContextMatches(context: ViewerOpenContext, props: ActiveContentViewerContextMatch): boolean {
  switch (context.kind) {
    case ViewerOpenContextKind.CHAT_ARTIFACT:
      return props.kind === ViewerOpenContextKind.CHAT_ARTIFACT && context.artifactVersionId === props.artifactVersionId

    case ViewerOpenContextKind.AUTO_OPEN:
      return props.kind === ViewerOpenContextKind.AUTO_OPEN && context.artifactVersionId === props.artifactVersionId

    case ViewerOpenContextKind.CHAT_CITATION:
      if (props.kind !== ViewerOpenContextKind.CHAT_CITATION) return false
      return props.highlightIndex === undefined
        ? context.citationId === props.citationId
        : context.citationId === props.citationId && context.highlightIndex === props.highlightIndex

    case ViewerOpenContextKind.MED_CHRON_REFERENCE:
      return props.kind === ViewerOpenContextKind.MED_CHRON_REFERENCE && context.referenceId === props.referenceId

    case ViewerOpenContextKind.MED_SUMMARY_REFERENCE:
      return props.kind === ViewerOpenContextKind.MED_SUMMARY_REFERENCE && context.referenceId === props.referenceId

    case ViewerOpenContextKind.MED_CHRON_SOURCE:
      return props.kind === ViewerOpenContextKind.MED_CHRON_SOURCE && context.sourceId === props.sourceId

    case ViewerOpenContextKind.MED_SUMMARY_SOURCE:
      return props.kind === ViewerOpenContextKind.MED_SUMMARY_SOURCE && context.sourceId === props.sourceId

    case ViewerOpenContextKind.TIMELINE_REFERENCE:
      return props.kind === ViewerOpenContextKind.TIMELINE_REFERENCE && context.referenceId === props.referenceId

    case ViewerOpenContextKind.TIMELINE_SOURCE:
      return props.kind === ViewerOpenContextKind.TIMELINE_SOURCE && context.sourceId === props.sourceId

    default:
      return false
  }
}

export const selectActiveContentViewerContextMatches = createSelector(
  selectActiveContentViewerSession,
  (_: RootState, props: ActiveContentViewerContextMatch) => props,
  (session, props) => {
    if (!session) return false
    return activeContextMatches(session.context, props)
  }
)
